import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  inject,
  signal,
  viewChild,
  effect,
  DestroyRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { LP_UI_CONFIG } from '../../core/tokens';
import { AdditionalInfo } from '../../models/classes/additionalinfo';
import { Asset } from '../../models/classes/asset';
import { JunctionBoxSlot } from '../../models/junctionbox.model';
import { FlatMarker, Marker } from '../../models/classes/marker';
import { EquipmentUtil } from '../../utilities/equipment.util';
import { ISensor } from '../../models/sensor.model';
import { SensorAdditionalField } from '../../models/classes/sensoradditionalfield';
import { AdditionalInfoService } from '../../services/additional-info.service';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { AssetsService } from '../../services/asset.service';
import { NavButtonComponent } from '../nav-button/nav-button';
import { HeaderComponent } from '../header/header';
import { MarkerImageComponent } from '../marker-image/marker-image';
import { ExitCreationPopupComponent } from '../exit-creation-popup/exit-creation-popup';
import { SpinnerComponent } from '../spinner/spinner';
import { FooterComponent } from '../footer/footer';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { DragMarkerImageComponent } from '../drag-marker/drag-marker';
import { EmbeddedSensorMapComponent } from '../embedded-sensor-map/embedded-sensor-map';
import { ImageCropComponent } from '../image-crop/image-crop';
import { MatchEquipmentComponent } from '../match-equipment/match-equipment';
import { GLOBAL_CONSTANT } from '../../constants/global.constant';
import { IsensorConnectPhoto } from '../../models/sensor.model';
import { App_Routes } from '../../enums/router.enum';

export enum MatchSensor {
  match,
  photo,
  photoSensorCrop,
  photoSensorEdit,
  photoAssetCrop,
  photoAssetEdit,
  location,
  end,
}

@Component({
  selector: 'app-match-sensor',
  templateUrl: './match-sensor.html',
  styleUrls: ['./match-sensor.scss'],
  imports: [
    NavButtonComponent,
    MatchEquipmentComponent,
    HeaderComponent,
    SpinnerComponent,
    MarkerImageComponent,
    ExitCreationPopupComponent,
    FooterComponent,
    NzIconModule,
    DragMarkerImageComponent,
    EmbeddedSensorMapComponent,
    ImageCropComponent,
  ],
})
export class MatchSensorComponent implements OnInit {
  // Services
  public cfg = inject(LP_UI_CONFIG);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private assetsService = inject(AssetsService);
  private additionalService = inject(AdditionalInfoService);
  private ui = inject(UiService);
  private destroyRef = inject(DestroyRef);
  public i18n = inject(I18nService);
  public fileEvent: any = GLOBAL_CONSTANT.EMPTY_STRING;
  sensorMarkerPlaced = false;
  hasCamera = false;

  // Signal Queries for ViewChild
  sensorImageInput = viewChild<ElementRef<HTMLInputElement>>('sensorImageInput');
  equipmentImageInput = viewChild<ElementRef<HTMLInputElement>>('equipmentImageInput');
  scanImageInput = viewChild<ElementRef<HTMLInputElement>>('scanImageInput');
  photoid = viewChild<ElementRef<HTMLElement>>('photoid');
  photoView = viewChild<ElementRef<HTMLElement>>('photoView');

  // Enums and Constants
  stages = MatchSensor;
  private defaultJunctionBoxSlot: JunctionBoxSlot = {
    displayOrder: 0,
    label: GLOBAL_CONSTANT.EMPTY_STRING,
    value: 'default',
  };

  // State Signals
  stage = signal<MatchSensor>(MatchSensor.match);
  saving = signal(false);
  plantId = signal<number>(0);
  assetId = signal<number>(0);
  asset = signal<Asset | null>(null);
  previousSensorId = signal<number | null>(null);
  sensorName = signal<string | undefined>(undefined);
  sensorPosition = signal<{ lat: number; lng: number } | null>(null);
  junctionBoxSlot = signal<JunctionBoxSlot>(this.defaultJunctionBoxSlot);
  existingMarkers = signal<{ sensorId: number; x: number; y: number; label: string }[]>([]);
  matchedEquipmentValues = signal<AdditionalInfo[]>([]);

  // Photo State Signals
  scanUploadPhoto = signal<IsensorConnectPhoto>(
    this.initPhotoState('assets/images/default_sensor.png')
  );
  sensorPhoto = signal<IsensorConnectPhoto>(
    this.initPhotoState('assets/images/default_sensor.png')
  );
  equipmentPhoto = signal<IsensorConnectPhoto>(
    this.initPhotoState('assets/images/default_image_equipment.png')
  );

  // UI / Overlay Signals
  overlayScanUploadPopup = signal(false);
  overlayExitPopup = signal(false);
  exitPopupOpenedViaBrowserBack = signal(false);
  showQrScanPopup = signal(false);

  // Computed Values
  isMobile = this.ui.IsMobile;

  // Private Variables (Non-reactive)
  private args: string | null = null;
  private redirectArgs: any = null;
  private scrollEvent: any;
  private scrollType: 'sensor' | 'equipment' | null = null;
  private previousSensor: any;
  private npEquipmentDetatil: any;
  counter: any;

  @HostListener('window:popstate', ['$event'])
  onPopState(event: Event) {
    event.preventDefault();
    this.exitPopupOpenedViaBrowserBack.set(true);
  }

  constructor() {
    // Effect for history management when assetId changes
    effect(() => {
      if (this.assetId()) {
        this.pushHistoryState();
      }
    });
  }

  goToEquipMonitor() {
    const id = this.asset()?.objectID;
    if (id) this.router.navigate([App_Routes.MATCH_SENSOR, id]);
    this.router.navigate([App_Routes.ASSET]);
  }

  ngOnInit() {
    this.route.paramMap.pipe().subscribe((params) => {
      const aId = params.get('assetId');
      const pId = params.get('previousSensorId');
      const argStr = params.get('args');

      if (aId) this.assetId.set(+aId);
      if (pId) this.previousSensorId.set(+pId);

      if (argStr) {
        this.args = argStr;
        this.redirectArgs = JSON.parse(atob(argStr));
      }

      this.refresh();
    });

    // Global Scroll Listener
    const onScroll = (evt: Event) => {
      this.onWindowScroll(false, evt);
      this.scrollEvent = evt;
    };
    window.addEventListener('scroll', onScroll, true);
    this.destroyRef.onDestroy(() => window.removeEventListener('scroll', onScroll, true));

    // Initial Data Fetch

    const id = this.assetId();
    if (id) {
      this.assetsService.getAdditionalInfo(id).then((res) => {
        this.matchedEquipmentValues.set(res.filter((r) => r.infoTypeID === 5));
      });
    }
  }

  isMatchComplete() {
    return this.assetsService.getRotatingEquipmentDetail();
  }

  private initPhotoState(defaultImg: string): IsensorConnectPhoto {
    return {
      useDefaultImage: false,
      defaultImage: defaultImg,
      imagePreview: GLOBAL_CONSTANT.EMPTY_STRING,
      imageFile: null,
      imageHeight: 0,
      imageWidth: 0,
      markers: [Marker.getDeselectedEmptyMarker()],
    };
  }

  refresh() {
    const result = this.assetsService.plantsState;
    const currentAssetId = this.assetId();
    if (!currentAssetId) return;

    const currentAsset = this.assetsService.getAsset(currentAssetId, result().plants);
    this.asset.set(currentAsset);

    const plant: any = this.assetsService.getPlantByAssetID(currentAssetId, result().plants);
    this.plantId.set(plant.objectID);

    // Update Photos based on Asset
    this.equipmentPhoto.update((p) => ({
      ...p,
      imagePreview: currentAsset.picture,
      useDefaultImage: !currentAsset.picture,
      defaultImage: EquipmentUtil.getDefaultImage(
        currentAsset.equipmentType,
        currentAsset.defaultImageId
      ),
    }));

    this.sensorPhoto.update((p) => ({
      ...p,
      defaultImage: EquipmentUtil.getDefaultImage(
        currentAsset.equipmentType,
        currentAsset.defaultImageId
      ),
    }));

    const prevId = this.previousSensorId();
    if (prevId) {
      this.previousSensor = this.assetsService.getSensorBySensorID(prevId);
      if (this.previousSensor) {
        this.sensorMarkerPlaced =
          !!this.previousSensor.equipmentSensorX || !!this.previousSensor.equipmentSensorY;

        this.sensorName.set(this.previousSensor.sensorName);

        this.sensorPhoto.update((p) => ({
          ...p,
          imagePreview: this.previousSensor!.picture,
          useDefaultImage: !this.previousSensor!.picture,
          markers: [
            new Marker(
              this.previousSensor!.sensorID,
              this.previousSensor!.markerText,
              this.previousSensor!.sensorX,
              this.previousSensor!.sensorY,
              0.2,
              false
            ),
          ],
        }));

        this.sensorPosition.set({
          lat: this.previousSensor.latitude,
          lng: this.previousSensor.longitude,
        });
      }
    }
    this.placeEquipmentMarkers();
  }

  placeEquipmentMarkers() {
    const currentAsset = this.asset();
    if (!currentAsset) return;

    const markers: any = currentAsset.sensors?.map((sensor: any) => ({
      sensorId: sensor.sensorID,
      x: sensor.equipmentSensorX,
      y: sensor.equipmentSensorY,
      label: sensor.equipmentMarkerText || sensor.markerText,
    }));
    this.existingMarkers.set(markers);
  }

  onRestart() {
    this.sensorPhoto.update((p) => ({
      ...p,
      useDefaultImage: false,
      imagePreview: '',
      imageFile: null,
      imageHeight: 0,
      imageWidth: 0,
    }));

    this.placeEquipmentMarkers();
    this.sensorName.set(undefined);
    this.junctionBoxSlot.set(this.defaultJunctionBoxSlot);
    this.stage.set(this.stages.match);
  }

  onNavToEM() {
    window.location.href = `${
      this.cfg.prefix_equipment_monitor_uri
    }asset/${this.assetId()}/${this.previousSensorId()}`;
  }

  getHeaderSteps(start = 0): string[] {
    const steps = [
      this.i18n.langString('match'),
      this.i18n.langString('photo'),
      this.i18n.langString('location'),
    ];
    return steps.slice(start, start + 3);
  }

  onHeaderNavigation(index: number) {
    const currentStage = this.stage();
    if (currentStage === this.stages.match) return;

    if (currentStage === this.stages.photo && index === 0) {
      this.stage.set(this.stages.match);
    } else if (currentStage === this.stages.location) {
      if (index === 0) this.stage.set(this.stages.match);
      if (index === 1) this.stage.set(this.stages.photo);
    }
  }

  topScroll(stageState: keyof typeof MatchSensor) {
    this.stage.set(this.stages[stageState]);
    this.onWindowScroll(true, this.scrollEvent);

    switch (stageState) {
      case 'match':
        this.counter = 0;
        break;
    }
  }

  isSensorComplete() {
    return this.assetId() && this.sensorPosition;
  }

  onPositionChanged(event: any) {
    this.sensorPosition.set(event);
  }

  onExit(nav: any) {
    this.overlayExitPopup.set(false);
    this.assetsService.setRotatingEquipmentDetail(null);

    if (nav) {
      this.onNavBack();
    } else {
      if (this.exitPopupOpenedViaBrowserBack()) {
        this.pushHistoryState();
      }
      this.exitPopupOpenedViaBrowserBack.set(false);
    }
  }

  onNavBack() {
    if (this.redirectArgs) {
      this.toEquipmentMonitor();
    } else if (this.assetId()) {
      this.router.navigate(['plant', this.plantId()]);
    } else {
      this.router.navigate(['home']);
    }
  }

  nameErrorCheck(): boolean {
    const name = this.sensorName();
    const currentAsset = this.asset();
    if (!name || !currentAsset) return true;

    const exists = currentAsset.sensors?.some(
      (s) =>
        s.sensorName.trim().toLowerCase() === name.trim().toLowerCase() &&
        s.sensorID !== this.previousSensorId()
    );
    return !exists;
  }

  openUpload() {
    this.overlayScanUploadPopup.set(false);
    this.scanUploadPhoto.update((p) => ({ ...p, useDefaultImage: false }));
    this.scanImageInput()?.nativeElement.click();
  }

  fileUploadedForScan(event: any) {
    this.fileEvent = event;
    const file = event.target.files?.[0];
    if (file) {
      this.scanUploadPhoto.update((p) => ({ ...p, imageFile: file }));
    }
  }

  confirmUploadImageCrop(croppedImage: string) {
    const input = this.scanImageInput();
    if (input) input.nativeElement.value = GLOBAL_CONSTANT.EMPTY_STRING;
    this.scanUploadPhoto.update((p) => ({
      ...p,
      useDefaultImage: false,
      imagePreview: croppedImage,
    }));
  }

  cancelUploadImageCrop() {
    this.scanImageInput()?.nativeElement.setRangeText(GLOBAL_CONSTANT.EMPTY_STRING, 0, 0); // Reset value workaround
    if (this.stage() === this.stages.photoSensorCrop) {
      const input = this.sensorImageInput();
      if (input) input.nativeElement.value = GLOBAL_CONSTANT.EMPTY_STRING;
      this.stage.set(this.stages.photo);
    }
    if (this.stage() === this.stages.photoAssetCrop) {
      const input = this.equipmentImageInput();
      if (input) input.nativeElement.value = GLOBAL_CONSTANT.EMPTY_STRING;
      this.stage.set(this.stages.photo);
    }
  }

  addImage(kind: 'sensor' | 'equipment') {
    const photoSignal = kind === 'sensor' ? this.sensorPhoto : this.equipmentPhoto;
    const input = kind === 'sensor' ? this.sensorImageInput() : this.equipmentImageInput();

    photoSignal.update((p) => ({ ...p, useDefaultImage: false }));
    input?.nativeElement.click();
    this.scrollType = kind;
  }

  fileChanged(kind: 'sensor' | 'equipment', event: any) {
    this.fileEvent = event;
    const file = event.target.files?.[0];
    const photoSignal = kind === 'sensor' ? this.sensorPhoto : this.equipmentPhoto;
    const input = kind === 'sensor' ? this.sensorImageInput() : this.equipmentImageInput();

    if (!file) {
      if (input) input.nativeElement.value = GLOBAL_CONSTANT.EMPTY_STRING;
      photoSignal.update((p) => ({ ...p, imageFile: null, imagePreview: '' }));
      return;
    }

    photoSignal.update((p) => ({ ...p, imageFile: file }));
    this.stage.set(kind === 'sensor' ? this.stages.photoSensorCrop : this.stages.photoAssetCrop);
  }

  confirmImageCrop(croppedImage: string) {
    if (this.stage() === this.stages.photoSensorCrop) {
      const input = this.sensorImageInput();
      if (input) input.nativeElement.value = GLOBAL_CONSTANT.EMPTY_STRING;
      this.sensorPhoto.update((p) => ({
        ...p,
        useDefaultImage: false,
        imagePreview: croppedImage,
      }));
      this.stage.set(this.stages.photoSensorEdit);
    } else {
      const input = this.equipmentImageInput();
      if (input) input.nativeElement.value = GLOBAL_CONSTANT.EMPTY_STRING;
      this.equipmentPhoto.update((p) => ({
        ...p,
        useDefaultImage: false,
        imagePreview: croppedImage,
      }));
      this.stage.set(this.stages.photoAssetEdit);
    }
  }

  onImageLoaded(kind: 'sensor' | 'equipment', event: any) {
    const target = event?.target;
    const photoSignal = kind === 'sensor' ? this.sensorPhoto : this.equipmentPhoto;
    const currentAsset = this.asset();

    if (target) {
      photoSignal.update((p) => ({
        ...p,
        imageWidth: target.naturalWidth,
        imageHeight: target.naturalHeight,
      }));
    }

    if (currentAsset?.sensors) {
      this.updateSensorMarker();

      if (!this.previousSensorId() && !this.existingMarkers().some((m) => m.sensorId <= 0)) {
        const marker: any = Marker.getDeselectedEmptyMarker();
        marker.label = this.junctionBoxSlot().label;
        this.equipmentPhoto.update((p) => ({
          ...p,
          markers: [...p.markers, marker],
        }));
      }

      if (this.previousSensorId() && kind === 'sensor' && this.previousSensor) {
        this.sensorPhoto.update((p) => ({
          ...p,
          markers: [
            new Marker(
              this.previousSensorId()!,
              'LABEL',
              this.previousSensor!.sensorX,
              this.previousSensor!.sensorY,
              0.2,
              false,
              p.imageWidth,
              p.imageHeight
            ),
          ],
        }));
      }
    }
  }

  loadDefaultImage(kind: 'sensor' | 'equipment') {
    const photoSignal = kind === 'sensor' ? this.sensorPhoto : this.equipmentPhoto;
    if (photoSignal().defaultImage) {
      photoSignal.update((p) => ({
        ...p,
        useDefaultImage: true,
        imagePreview: '',
        imageFile: null,
        imageHeight: 0,
        imageWidth: 0,
      }));
    }
  }

  confirmImageMarker() {
    this.stage.set(this.stages.photo);

    const photo = this.equipmentPhoto();
    const markers = photo.markers.map((marker) => ({
      sensorId: marker.sensorID,
      x: marker.getAbsoluteX(photo.imageWidth),
      y: marker.getAbsoluteY(photo.imageHeight),
      label: marker.label,
    }));

    this.existingMarkers.set(markers);
    this.updateSensorMarker();

    const viewEl = this.scrollType === 'equipment' ? this.photoView() : this.photoid();
    if (viewEl) this.scrollIntoView(viewEl.nativeElement);
  }

  updateSensorMarker() {
    const photo = this.equipmentPhoto();
    this.equipmentPhoto.update((p) => ({
      ...p,
      markers: this.existingMarkers().map(
        (marker) =>
          new Marker(
            marker.sensorId,
            marker.label,
            marker.x,
            marker.y,
            0.2,
            false,
            photo.imageWidth,
            photo.imageHeight
          )
      ),
    }));
  }

  createSensor() {
    this.saving.set(true);
    this.npEquipmentDetatil = this.assetsService.getRotatingEquipmentDetail();
    if (!this.npEquipmentDetatil) return;

    const markersList: FlatMarker[] = [];
    let addedMarker: FlatMarker | undefined;

    this.existingMarkers().forEach((marker) => {
      if (marker.sensorId > 0 && marker.sensorId !== this.previousSensorId()) {
        markersList.push({
          sensorId: marker.sensorId,
          x: marker.x,
          y: marker.y,
        });
      } else {
        addedMarker = { sensorId: marker.sensorId, x: marker.x, y: marker.y };
      }
    });

    const sensorAdditionals = this.getSensorAdditionalFields(0);
    const assetInfoData = this.saveAssetInfoData();

    const sensorCall = this.previousSensorId()
      ? this.reconnectSensorCall(addedMarker!)
      : this.createSensorCall(addedMarker!, sensorAdditionals);

    forkJoin([
      sensorCall,
      this.assetsService.updateEquipment(
        this.assetId()!,
        this.equipmentPhoto().imageFile?.name || GLOBAL_CONSTANT.EMPTY_STRING,
        this.equipmentPhoto().imagePreview?.split(',')[1] || GLOBAL_CONSTANT.EMPTY_STRING,
        this.asset()!.objectName,
        this.asset()!.equipmentType,
        this.equipmentPhoto().useDefaultImage,
        this.asset()!.picture,
        this.asset()!.defaultImageId || GLOBAL_CONSTANT.EMPTY_STRING
      ),
      this.assetsService.updateEquipmentMarker(this.asset()!.objectID, markersList),
      this.assetsService.updateAdditionalInfo(assetInfoData),
    ]).subscribe(([sensorRes]) => {
      this.saving.set(false);
      this.stage.set(this.stages.end);
      this.asset()?.sensors?.forEach((sensor) => {
        if (sensor.sensorID < 0) sensor.sensorID = sensorRes[0].sensorID;
      });

      if (this.redirectArgs) {
        this.toEquipmentMonitor();
      } else {
        this.previousSensorId.set(sensorRes[0].sensorID);
      }
    });
  }

  private getSensorAdditionalFields(sensorID: number) {
    if (!this.npEquipmentDetatil) return [];
    return [
      new SensorAdditionalField(sensorID, 'PlantName', this.npEquipmentDetatil.plantName),
      new SensorAdditionalField(sensorID, 'PlantId', this.npEquipmentDetatil.plantId.toString()),
      new SensorAdditionalField(sensorID, 'EquipmentName', this.npEquipmentDetatil.equipmentName),
      new SensorAdditionalField(
        sensorID,
        'EquipmentId',
        this.npEquipmentDetatil.equipmentId.toString()
      ),
      new SensorAdditionalField(sensorID, 'CompanyName', this.npEquipmentDetatil.companyName),
      new SensorAdditionalField(
        sensorID,
        'ComponentId',
        this.npEquipmentDetatil.componentId.toString()
      ),
    ];
  }

  private saveAssetInfoData() {
    const assetInfoData = this.additionalService.getRotatingEquipmentDefaults(
      this.i18n.measureMentSystem()
    );
    const equipmentNameID = 25;
    const plantNameID = 26;

    assetInfoData.forEach((data: any) => {
      data.objectID = this.assetId()!;
      data.categoryID = data['fieldID'];

      const matchedValues = this.matchedEquipmentValues();
      if (matchedValues.length > 0) {
        const addInfoToUpdate = matchedValues.find((v) => data.categoryID === v.categoryID);
        if (addInfoToUpdate) data.additionalInfoID = addInfoToUpdate.additionalInfoID;
      }

      if (this.npEquipmentDetatil) {
        if (data.categoryID === equipmentNameID) data.text = this.npEquipmentDetatil.equipmentName;
        if (data.categoryID === plantNameID) data.text = this.npEquipmentDetatil.plantName;
      }
    });
    return assetInfoData;
  }

  private toEquipmentMonitor() {
    window.location.href = `${this.cfg.prefix_equipment_monitor_uri}/${this.redirectArgs.returnUrl}`;
  }

  private createSensorCall(
    addedMarker: FlatMarker,
    additionalInfo: SensorAdditionalField[]
  ): Promise<ISensor[]> {
    const photo = this.sensorPhoto();
    const pos = this.sensorPosition();
    return this.assetsService.createSensor(
      this.assetId(),
      photo.imageFile?.name || GLOBAL_CONSTANT.EMPTY_STRING,
      photo.imagePreview?.split(',')[1] || GLOBAL_CONSTANT.EMPTY_STRING,
      this.npEquipmentDetatil!.equipmentName,
      this.npEquipmentDetatil!.plantId,
      this.npEquipmentDetatil!.equipmentId,
      photo.markers[0].getAbsoluteX(photo.imageWidth),
      photo.markers[0].getAbsoluteY(photo.imageHeight),
      addedMarker?.x || 0,
      addedMarker?.y || 0,
      this.junctionBoxSlot().label || GLOBAL_CONSTANT.EMPTY_STRING,
      this.npEquipmentDetatil!.componentId.toString(),
      `${pos?.lat};${pos?.lng}`,
      this.previousSensorId(),
      1009,
      additionalInfo
    );
  }

  private reconnectSensorCall(addedMarker: FlatMarker): Promise<ISensor[]> {
    const photo = this.sensorPhoto();
    const pos = this.sensorPosition();
    return this.assetsService.reconnectSensor(
      this.assetId(),
      this.previousSensorId(),
      photo.useDefaultImage,
      this.previousSensor!.picture,
      photo.imageFile?.name || GLOBAL_CONSTANT.EMPTY_STRING,
      photo.imagePreview?.split(',')[1] || GLOBAL_CONSTANT.EMPTY_STRING,
      this.sensorName() || GLOBAL_CONSTANT.EMPTY_STRING,
      this.npEquipmentDetatil!.plantId,
      this.npEquipmentDetatil!.equipmentId,
      photo.markers[0].getAbsoluteX(photo.imageWidth),
      photo.markers[0].getAbsoluteY(photo.imageHeight),
      addedMarker?.x || 0,
      addedMarker?.y || 0,
      this.junctionBoxSlot().label || GLOBAL_CONSTANT.EMPTY_STRING,
      this.npEquipmentDetatil!.componentId.toString(),
      `${pos?.lat};${pos?.lng}`
    );
  }

  private scrollIntoView(el: HTMLElement) {
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 0);
  }

  private onWindowScroll(isNext: boolean, e: any): void {
    if (e != undefined) {
      let target = e.target;
      if (!target || (!target.scrollTop && target.scrollTop !== 0)) {
        target = document.getElementById('main')?.parentElement;
      }
      if (isNext && target) {
        target.scrollTop = 0;
      }
    }
  }

  private pushHistoryState() {
    const url = `match-sensor/${this.assetId()}${
      this.previousSensorId() ? '/' + this.previousSensorId() : GLOBAL_CONSTANT.EMPTY_STRING
    }${this.args ? '/' + this.args : GLOBAL_CONSTANT.EMPTY_STRING}`;
    history.pushState({}, 'match-sensor', url);
  }
}
